/**
 * Vessel domain types — frontend mirrors of the NestJS Vessel model
 * (Phase 2b-2 + Phase 5b additions).
 *
 * Numeric fields the backend stores as Decimal (lengthM, beamM) are
 * serialized over JSON as strings to preserve precision; the form layer
 * coerces them to numbers via zod (see schema.ts).
 */

export const VESSEL_CATEGORIES = ['civilian', 'armed'] as const;
export type VesselCategory = (typeof VESSEL_CATEGORIES)[number];

export const SENSITIVITY_LEVELS = [
  'unclassified',
  'restricted',
  'commercially_sensitive',
] as const;
export type SensitivityLevel = (typeof SENSITIVITY_LEVELS)[number];

export const CLASS_SOCIETIES = ['BV', 'DNV', 'LR', 'ABS', 'RINA', 'CCS'] as const;
export type ClassSociety = (typeof CLASS_SOCIETIES)[number];

export interface Vessel {
  id: string;
  companyId: string;

  // Identification
  name: string;
  imo: string | null;
  mmsi: string | null;
  callSign: string | null;
  flag: string | null;

  // Category + sensitivity
  category: VesselCategory;
  sensitivityLevel: SensitivityLevel;
  nsnOtan: string | null;
  displayLabel: string | null;

  // Physical
  lengthM: string | null;
  beamM: string | null;
  grossTonnage: number | null;
  yearBuilt: number | null;
  shipyard: string | null;

  // Classification
  classSociety: ClassSociety | null;
  classNumber: string | null;
  /** L1.2 — true when the vessel is currently surveyed by
   *  classSociety (toggled independently of classSociety enrolment). */
  classSurveyed: boolean;

  /** L1.2 — MinIO/S3 key (relative to MINIO_BUCKET) for the
   *  vessel's current photo, or null if none. */
  photoKey: string | null;

  // Operational
  homePort: string | null;
  isActive: boolean;

  /** L4 — fleet-wide warranty window. Equipment with
   *  `warrantySource = INHERITED` follows these dates via the
   *  service-layer cascade on update. */
  warrantyStartDate: string | null;
  warrantyEndDate: string | null;

  // Audit
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Display helper used in lists and breadcrumbs. */
export function vesselDisplayName(v: Vessel): string {
  return v.displayLabel ?? v.name;
}
